/** Contenuti in italiano. I nomi dei modi seguono il catalogo `it.json`. */
import type { LanguageContent } from '../registry.js';

export const IT: LanguageContent = {
  home: {
    intro: [
      'Retusch è un gioco di disegno online per 2 a 10 giocatori, si gioca nel browser, senza installare nulla e senza account. Ogni round ti consegna un quadro a cui è stata cancellata una casella: ridipingila perché sparisca dentro l’opera. Poi le parti si invertono, e tocca stanare i ritocchi degli altri.',
      'Quasi tutti i giochi di disegno premiano chi viene riconosciuto prima. Retusch chiede il contrario: vincere significa non essere visti.',
    ],
    steps: [
      {
        title: 'Un quadro con un buco',
        text: 'Ricevi un’illustrazione a cui è stata cancellata una casella.',
        painting: '1_airIsland',
        stage: 'masked',
      },
      {
        title: 'La ridipingi',
        text: 'Ridisegni la casella perché si fonda con l’opera.',
        painting: '1_airIsland',
        stage: 'painted',
      },
      {
        title: 'La caccia',
        text: 'Il quadro torna con i ritocchi di tutti: trovali prima degli altri.',
        painting: '1_airIsland',
        stage: 'hunt',
      },
      {
        title: 'La rivelazione',
        text: 'Ogni nascondiglio viene scoperto, e i punti cadono.',
        painting: '1_airIsland',
        stage: 'reveal',
      },
    ],
    modes: [
      {
        name: 'Caccia comune',
        text: 'Tutti dipingono sullo stesso quadro, ciascuno nella propria casella. Poi la caccia si apre per tutti insieme: un ritocco si trova una volta sola, da chi lo individua per primo. È il modo predefinito, quello in cui succede sempre qualcosa.',
      },
      {
        name: 'Stream',
        text: 'Chi ospita non disegna: cerca, da solo, mentre tutti gli altri si nascondono nel suo quadro. Pensato per la diretta, dove la caccia diventa lo spettacolo. Gratis per sempre.',
      },
      {
        name: 'Un quadro a testa',
        text: 'Un’opera per giocatore. Cerchi il ritocco di una sola persona, con un solo clic. La regola più semplice del gioco, e la più tesa.',
      },
      {
        name: 'Disegna uno solo',
        text: 'Un round, un disegnatore, tutti gli altri all’inseguimento. Il ruolo ruota, e il disegnatore guarda il proprio nascondiglio cadere o resistere.',
      },
      {
        name: 'Battle royale',
        text: 'La stessa caccia, ma il primo ritocco scoperto elimina il suo autore. Si gioca fino all’ultimo rimasto, senza contare un solo punto.',
      },
      {
        name: 'Concorso',
        text: 'Nessuno cerca. Tutti ridipingono la stessa casella dello stesso quadro, poi ciascuno vota il ritocco che preferisce. Un voto ricevuto vale un punto.',
      },
    ],
    faq: [
      {
        question: 'Quanti giocatori servono?',
        answer:
          'Da 2 a 10. Se sei da solo, un avversario automatico occupa il posto libero perché la partita inizi subito. Da quattro in su, la caccia comune dà il meglio di sé.',
      },
      {
        question: 'È gratis?',
        answer:
          'Sì. La partita rapida, la caccia comune e il modo stream sono gratuiti, senza limiti di tempo né di numero di partite. Anche gli altri modi lo sono, per ora.',
      },
      {
        question: 'Serve un account?',
        answer:
          'No. Scrivi un nome, fai clic, giochi. L’account serve solo a conservare nome e avatar da una partita all’altra: resta facoltativo.',
      },
      {
        question: 'Funziona sul telefono?',
        answer:
          'Sì, l’interfaccia si adatta al tocco: barra degli strumenti richiusa, lente sotto il dito, doppio tocco per ingrandire. Detto questo, si disegna con più precisione con il mouse e su uno schermo grande.',
      },
      {
        question: 'Come invito i miei amici?',
        answer:
          'Crea una stanza, copia il link d’invito e incollalo dove vuoi. I tuoi amici arrivano direttamente al tavolo, senza account e senza installare nulla.',
      },
      {
        question: 'Che cos’è un «ritocco»?',
        answer:
          'È quello che dipingi dentro la casella cancellata. Riuscito, passa per un pezzo del quadro originale e nessuno lo trova. Sbagliato, salta all’occhio.',
      },
    ],
    headings: {
      intro: 'Un gioco di disegno in cui bisogna sparire',
      steps: 'Come si gioca',
      modes: 'Sei modi di giocare',
      faq: 'Domande frequenti',
      more: 'Per saperne di più',
    },
    links: {
      rules: 'Leggi le regole complete e il punteggio',
      modes: 'Confronta i modi nel dettaglio',
    },
  },

  howToPlay: {
    title: 'Come giocare a Retusch',
    summary:
      'Le regole complete di Retusch: lo svolgimento di un round, il punteggio da entrambe le parti, gli strumenti di disegno e i consigli per nascondersi e per cercare.',
    sections: [
      {
        heading: 'L’idea in tre frasi',
        blocks: [
          'Ti viene dato un quadro a cui è stata cancellata una casella, e la ridipingi perché passi inosservata.',
          'Poi tutti guardano i quadri ritoccati e cercano le caselle ridipinte.',
          'Fai punti trovando in fretta, e ne fai anche finché il tuo nascondiglio resiste.',
        ],
      },
      {
        heading: 'Lo svolgimento di un round',
        blocks: [
          'Una partita concatena round identici, cinque per impostazione predefinita. Ognuno passa per tre momenti.',
          {
            list: [
              'Il disegno. Ricevi il tuo quadro e la tua casella, e hai 2 minuti per impostazione predefinita per ridipingerla. Il pulsante Fatto vale come consegna: appena tutti hanno consegnato, il round prosegue senza aspettare il cronometro.',
              'La caccia. Il quadro riappare con i ritocchi, e hai 30 secondi per impostazione predefinita per individuarli. Un clic su una casella ridipinta la rivela a tutto il tavolo.',
              'La rivelazione. Tutti i nascondigli vengono incorniciati e nominati, compresi quelli che nessuno ha visto. Un cursore permette di confrontare il quadro ritoccato con l’opera originale.',
            ],
          },
        ],
      },
      {
        heading: 'Il punteggio',
        blocks: [
          'Due contatori corrono insieme, e si oppongono: chi cerca vuole fare in fretta, chi ha disegnato vuole che la sua casella resista.',
          {
            list: [
              'Per chi cerca: 1000 punti se trovi nei primi 3 secondi, poi un calo regolare fino a 100 punti alla fine della fase. Sbagliare o non trovare nulla non dà niente.',
              'Per chi disegna: solo 100 punti se la tua casella cade subito, poi una crescita regolare fino a 800 punti se resiste fino in fondo. Un ritocco che nessuno trova vale il massimo, 800 punti.',
            ],
          },
          'Il disegnatore viene pagato una sola volta, alla rivelazione, e in base al cercatore più veloce. Non fa differenza, quindi, che ti cerchino in due o in otto: conta solo chi ti trova per primo.',
          'Il punteggio è lo stesso qualunque durata tu imposti per la fase. Allungare la caccia non sposta i due estremi, solo la pendenza fra loro.',
          'Un solo errore per round. Il primo clic che non tocca alcun ritocco chiude la tua caccia: senza questa regola si setaccerebbe l’immagine a caso finché non capita di trovarne uno. Due gesti non costano nulla: cliccare sulla propria casella, e cliccare su un ritocco già trovato.',
        ],
      },
      {
        heading: 'Gli strumenti di disegno',
        blocks: [
          {
            list: [
              'Il pennello (B): tre misure rapide, più un cursore per la misura esatta. Le scorciatoie 1, 2 e 3 scelgono le tre misure.',
              'Il secchiello (P): riempie in un colpo una superficie piatta, senza mai uscire dalla tua casella.',
              'Il contagocce (I): preleva un colore in qualsiasi punto del quadro. Tenere premuto Alt ci passa per un clic e restituisce poi lo strumento precedente.',
              'La mano (H): sposta la vista. Fuori dalla tua casella, pennello e secchiello fanno lo stesso, quindi non hai quasi mai bisogno di cambiare strumento.',
              'Annulla (Ctrl+Z o Cmd+Z): torna indietro un tratto alla volta, fino a trenta volte. Non c’è il ripristino.',
              'Il colore di fondo, in fondo alla tavolozza: è il colore iniziale della tua casella. Dipingere con quello equivale a cancellare, con la differenza che è un tratto come gli altri, che si annulla e si copre. Per questo non esiste una gomma.',
            ],
          },
        ],
      },
      {
        heading: 'Cinque consigli per nascondersi',
        blocks: [
          {
            list: [
              'Preleva i colori, non indovinarli. Il contagocce dà il colore esatto di ciò che sta intorno: una campitura appena fuori si nota da lontano, mentre una esatta diventa invisibile.',
              'Prolunga ciò che attraversa la tua casella. Un muro, un orizzonte, un ramo che entra da un lato deve uscire dall’altro nel punto giusto. Una linea interrotta è il primo indizio che cerca un giocatore esperto.',
              'Dipingi prima gli angoli. È l’errore più comune: il centro è curato, i quattro angoli restano al fondo originale, e il quadrato si vede a occhio nudo senza nemmeno guardare il disegno.',
              'Vai dal generale al dettaglio. Posa prima le grandi masse di colore con un pennello grosso, poi rifinisci. Al contrario ti mangi tutto il tempo su un angolo mentre il resto resta vuoto.',
              'Copia il livello di dettaglio di ciò che ti circonda. Una casella troppo pulita in mezzo a una zona carica si vede quanto una fatta male in mezzo a una campitura piatta.',
            ],
          },
        ],
      },
      {
        heading: 'Quattro consigli per cercare',
        blocks: [
          {
            list: [
              'Cerca le linee che si interrompono. Un bordo, un cavo, lo spigolo di un tavolo che si sposta di un capello: quasi sempre è lì.',
              'Prima scorri, poi ingrandisci. A vista intera, una casella mal fusa appare come una macchia. Lo zoom serve a confermare, non a cercare.',
              'Diffida del troppo liscio. Una superficie perfettamente uniforme in mezzo a una texture è sospetta quanto una sbavatura.',
              'Non cliccare a caso. Hai diritto a un solo errore: meglio perdere tre secondi a controllare che perdere il round intero.',
            ],
          },
        ],
      },
      {
        heading: 'E a seconda del modo?',
        blocks: [
          'Tutto quanto sopra descrive la caccia comune, il modo predefinito. Gli altri modi cambiano chi disegna, chi cerca e come si vince: il dettaglio di ciascuno è nella pagina dei modi di gioco.',
        ],
      },
    ],
  },

  modes: {
    title: 'I modi di gioco di Retusch',
    summary:
      'I sei modi di giocare a Retusch: caccia comune, stream, un quadro a testa, disegna uno solo, battle royale e concorso. Svolgimento, numero di giocatori consigliato e durata.',
    sections: [
      {
        heading: 'Caccia comune',
        blocks: [
          'Il modo predefinito, e quello da cui conviene partire.',
          'Tutti disegnano sullo stesso quadro, ciascuno nella propria casella. Quando i ritocchi sono consegnati, l’opera torna intera e la caccia si apre per tutti insieme. Un ritocco si trova una volta sola, da chi lo individua per primo: compare allora per tutto il tavolo ed esce dal gioco. Il proprio non si trova mai, e un nascondiglio che nessuno vede dà il massimo al suo autore.',
          'Ideale per un gruppo numeroso: non ci sono tempi morti, tutti cercano insieme e ogni scoperta appare sullo schermo di tutti.',
          'Consigliato: da 3 a 10 giocatori.',
        ],
      },
      {
        heading: 'Stream',
        blocks: [
          'L’immagine speculare di «disegna uno solo», pensata per la diretta.',
          'Tutti i giocatori disegnano sullo stesso quadro, tranne chi ospita. Poi chi ospita è l’unico a cercare, mentre il suo pubblico lo guarda faticare. Non fa punti e non compare in classifica: la partita si gioca fra chi si nasconde, lui conduce la caccia. Può sbagliare quante volte vuole, la regola dell’errore unico non vale per lui.',
          'Ideale per una serata in diretta, e per un motivo preciso: siccome chi ospita non disegna, gli spettatori non vedono mai costruirsi il suo nascondiglio. Negli altri modi lo guardano dipingere la propria casella, e la caccia non ha più segreti per loro.',
          'Consigliato: da 4 a 10 giocatori. Gratis, e resterà così.',
        ],
      },
      {
        heading: 'Un quadro a testa',
        blocks: [
          'Ogni giocatore riceve la propria opera e ci nasconde il suo ritocco. Al momento della caccia ti viene consegnato il quadro di una sola altra persona, e hai un unico clic per indicare la casella ridipinta. Nessuna corsa, nessuna concorrenza: solo tu, un quadro e una decisione.',
          'Ideale in due, o quando si vuole una partita tranquilla in cui ciascuno si prende il suo tempo.',
          'Consigliato: da 2 a 10 giocatori.',
        ],
      },
      {
        heading: 'Disegna uno solo',
        blocks: [
          'Un round, un disegnatore, tutti gli altri all’inseguimento.',
          'A ogni round, una sola persona riceve un quadro. Gli altri aspettano, poi cercano il suo ritocco, ciascuno valutato sul proprio tempo. Il disegnatore è valutato sul cercatore più veloce, e rivede il proprio nascondiglio congelato mentre lo si cerca. Il ruolo ruota: qui l’impostazione dei round conta giri completi, perché una partita è equa solo se ciascuno ha disegnato lo stesso numero di volte.',
          'Ideale per un gruppo piccolo che voglia misurarsi ad armi pari, un disegno dopo l’altro.',
          'Consigliato: da 3 a 6 giocatori, oltre l’attesa si fa lunga.',
        ],
      },
      {
        heading: 'Battle royale',
        blocks: [
          'La caccia comune, con un’eliminazione per round.',
          'Stesso quadro condiviso, stessa caccia simultanea. Ma qui non si conta alcun punto: l’unica domanda è chi resta. Il giocatore il cui ritocco viene scoperto per primo lascia la partita. Gli altri continuano a cercare fino alla fine del round, il che non elimina più nessuno ma dà a ciascuno la sua posizione, mostrata sulla sua casella. Se non viene trovato nessuno, nessuno esce.',
          'Ideale per chiudere una serata: la tensione sale mentre il tavolo si svuota, e l’ultimo round si gioca in due.',
          'Consigliato: da 4 a 10 giocatori.',
        ],
      },
      {
        heading: 'Concorso',
        blocks: [
          'L’unico modo in cui nessuno cerca.',
          'Tutti ridipingono la stessa casella dello stesso quadro. Poi ciascuno scorre i ritocchi degli altri, con i suoi tempi, e vota quello che gli sembra più riuscito. Un voto ricevuto vale un punto al suo autore. Durante il voto gli autori sono anonimi, e non si può votare per sé: nomi e voti escono solo alla rivelazione.',
          'Ideale quando il gruppo è misto, o quando qualcuno preferisce disegnare senza la pressione del cronometro della caccia.',
          'Consigliato: da 3 a 10 giocatori.',
        ],
      },
    ],
  },
  modeTable: [
    { name: 'Caccia comune', players: 'da 3 a 10', duration: '15-20 min' },
    { name: 'Stream', players: 'da 4 a 10', duration: '15-20 min' },
    { name: 'Un quadro a testa', players: 'da 2 a 10', duration: '15-20 min' },
    { name: 'Disegna uno solo', players: 'da 3 a 6', duration: '20-35 min' },
    { name: 'Battle royale', players: 'da 4 a 10', duration: '20-30 min' },
    { name: 'Concorso', players: 'da 3 a 10', duration: '10-15 min' },
  ],
  modeTableHeadings: ['Modo', 'Giocatori', 'Durata'],
  premium: {
    heading: 'Modi gratuiti e modi a pagamento',
    blocks: [
      'La caccia comune e il modo stream sono gratuiti, in via definitiva.',
      'Gli altri quattro diventeranno a pagamento alla CREAZIONE di una partita, e solo allora. Unirsi resterà sempre gratis: basta che una persona al tavolo abbia sbloccato il gioco perché tutti possano giocare a tutti i modi, senza account e senza pagare.',
      'Lo sblocco sarà un acquisto unico, senza abbonamento, e toglierà anche la pubblicità per l’account che l’ha effettuato. Nel frattempo tutti i modi sono aperti a tutti.',
    ],
  },

  blog: [
    {
      slug: 'quest-ce-que-retusch',
      title: 'Che cos’è Retusch?',
      date: '2026-08-21',
      summary:
        'Un gioco di disegno online per più persone, vicino a Gartic Phone nella forma, ma la cui meccanica ricorda quella di Meccha Chameleon: invece di farsi riconoscere, si cerca di sparire.',
      sections: [
        {
          heading: 'Un gioco di disegno in cui farsi riconoscere è perdere',
          blocks: [
            'Retusch è un gioco di disegno online per 2 a 10 giocatori. Ogni round ti consegna un’illustrazione a cui è stata cancellata una casella, e devi ridipingerla perché si fonda con l’opera. Poi tutti guardano i quadri ritoccati e cercano le caselle ridipinte.',
            'Ciò che lo distingue sta in una frase: vincere significa non essere visti.',
          ],
        },
        {
          heading: 'Che cosa condivide con Gartic Phone',
          blocks: [
            'La forma è la stessa: apri un link, scrivi un nome, giochi. Nessun account, nessuna installazione, nessun client da scaricare. Il gioco gira nel browser e una partita parte in pochi secondi. Si invitano gli amici incollando un link in una conversazione, e il tavolo si riempie.',
            'Anche il ritmo è familiare: round brevi, un cronometro, una fase di disegno e poi una fase in cui si scopre che cosa hanno prodotto gli altri. E soprattutto quella rivelazione collettiva a fine round in cui tutti commentano quello che vedono. È lo stesso piacere di gruppo.',
            'Come in Gartic Phone, non serve saper disegnare. Una casella di cielo ben posata batte un capolavoro che sborda.',
          ],
        },
        {
          heading: 'Che cosa prende da Meccha Chameleon',
          blocks: [
            'Nel profondo, però, la meccanica sta altrove. In Meccha Chameleon, gioco tridimensionale su Steam, dipingi il tuo personaggio e poi lo collochi in uno scenario sperando che nessuno lo distingua da ciò che lo circonda. La vittoria viene dalla somiglianza e dalla buona collocazione, non dall’azione.',
            'Retusch riprende quell’idea e la riporta all’immagine. Non scegli dove nasconderti, la casella te la dà il gioco; in compenso fabbrichi interamente ciò che ci andrà dentro. Il nascondiglio riesce se lo sguardo ci scivola sopra senza fermarsi.',
            'E come in ogni gioco di mimetismo, la tensione nasce dal doppio ruolo. Sei nascosto e cacciatore insieme: mentre scruti il quadro in cerca di una campitura sospetta, qualcuno scruta il tuo.',
          ],
        },
        {
          heading: 'Quello che non esiste da nessun’altra parte',
          blocks: [
            'Il punteggio premia i due campi in modo opposto. Chi cerca fa più punti quanto prima trova; chi ha disegnato ne fa di più quanto più la sua casella resiste. Un ritocco che nessuno vede dà il massimo al suo autore.',
            'Ne nasce un modo di giocare molto particolare: non si cerca di fare bello, si cerca di fare esatto. Prelevare il colore giusto, prolungare una linea nel punto giusto, non lasciare nessun angolo vuoto. Sono gesti di osservazione prima ancora che di disegno.',
            'Per capirlo in pratica, la cosa più semplice è leggere le regole complete, oppure lanciare una partita rapida: parte senza impostazioni e senza attesa.',
          ],
        },
      ],
    },
    {
      slug: 'comment-etre-imbattable',
      title: 'Come diventare imbattibili a Retusch',
      date: '2026-08-21',
      summary:
        'Le scorciatoie da tastiera, il metodo dal generale al dettaglio e l’errore degli angoli vuoti: tre cose che separano un principiante da un giocatore che non si riesce più a trovare.',
      sections: [
        {
          heading: 'Impara le scorciatoie e raddoppi il tempo utile',
          blocks: [
            'Due minuti di disegno passano molto in fretta quando si va a prendere ogni strumento con il mouse. Le scorciatoie recuperano facilmente venti o trenta secondi a round, e sono proprio i secondi che dedicherai ai dettagli.',
            {
              list: [
                'B per il pennello, P per il secchiello, I per il contagocce, H per la mano.',
                '1, 2 e 3 per le tre misure di pennello, senza passare dalla barra degli strumenti.',
                'Ctrl+Z, o Cmd+Z su Mac, per annullare un tratto alla volta.',
                'Tenere premuto Alt passa al contagocce e restituisce lo strumento precedente appena lo rilasci. È il più utile di tutti: prelevi un colore in piena azione senza mai cambiare strumento.',
              ],
            },
            'Una nota sulle tastiere AZERTY: i tasti 1, 2 e 3 funzionano senza Maiusc, anche se la tua tastiera ci scrive &, é e ". Il gioco legge la posizione del tasto, non il carattere che produce.',
          ],
        },
        {
          heading: 'Dipingi dal generale al dettaglio',
          blocks: [
            'L’errore classico del principiante è partire da un angolo e curarlo. Il cronometro finisce, tre quarti della casella sono ancora fondo, e il ritocco si vede da dieci metri.',
            'Il metodo giusto è l’opposto. Comincia con un pennello grosso, o con il secchiello, e posa le grandi masse di colore: il cielo, il muro, l’ombra a terra. In venti secondi la tua casella ha già l’aspetto generale giusto e non si legge più come un blocco estraneo.',
            'Solo allora riduci la misura e tratta i passaggi: la linea d’orizzonte che attraversa, il bordo di un oggetto, la sfumatura che cambia a metà altezza. Se il tempo manca, avrai perso dettaglio, non il round.',
            'Preleva sempre i colori con il contagocce invece di sceglierli nella tavolozza. Una tinta approssimativa basta a tradire una casella intera, mentre la tinta esatta la rende invisibile.',
          ],
        },
        {
          heading: 'Non lasciare mai gli angoli vuoti',
          blocks: [
            'È l’indizio numero uno, e il primo che cercano i giocatori esperti. Una casella con il centro ben trattato ma i quattro angoli rimasti al colore di fondo disegna un rettangolo perfetto sullo schermo. Non serve nemmeno guardare il disegno: la forma geometrica salta all’occhio.',
            'Prendi l’abitudine di trattare prima il contorno e poi il centro. Una cornice ben fusa perdona un interno approssimativo; il contrario non è mai vero.',
            'Sborda di proposito sui bordi. Il gioco taglia il tuo tratto al limite della casella, quindi non rischi nulla ad andare ben oltre, ed è l’unico modo per essere certi che nessun pixel di fondo sopravviva in un angolo.',
          ],
        },
        {
          heading: 'E quando tocca a te cercare',
          blocks: [
            'Scorri prima il quadro intero senza ingrandire: una casella mal fusa sembra una macchia, e lo zoom serve solo a confermare. Poi segui le linee lunghe, quelle che attraversano l’immagine: un bordo spostato di un capello è quasi sempre un ritocco.',
            'E ricorda che hai diritto a un solo errore per round. Tre secondi di verifica costano meno del round intero.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-entre-amis',
      title: 'Un gioco di disegno da fare tra amici',
      date: '2026-08-21',
      summary:
        'Perché Retusch funziona bene in gruppo piccolo, come invitare incollando un link, e perché basta un giocatore per sbloccare i modi a pagamento per tutto il tavolo.',
      sections: [
        {
          heading: 'Un gioco che non chiede niente a nessuno',
          blocks: [
            'Il più grande ostacolo di una serata di giochi a distanza è l’installazione. Un account da creare, un client da scaricare, una versione che non corrisponde: metà del gruppo molla prima di giocare.',
            'Retusch gira nel browser. I tuoi amici cliccano un link, scrivono un nome e si ritrovano al tavolo. Non c’è niente da installare, niente da creare, e funziona bene sia sul computer sia sul telefono.',
            'Un’altra cosa che conta in gruppo: nessuno resta fuori perché non sa disegnare. Il gioco non chiede di rappresentare qualcosa, solo di far combaciare una casella con quello che la circonda. Chi disegna male ma osserva bene vince spesso.',
          ],
        },
        {
          heading: 'Invitare, in pratica',
          blocks: [
            'Crea una stanza dalla schermata iniziale. Togli la spunta a «stanza pubblica» se volete restare tra voi: l’invito tramite link diventa allora l’unica via d’ingresso.',
            'Copia il link con il pulsante apposito, poi incollalo nella vostra chat di gruppo, nel vostro canale vocale o nella vostra messaggistica. Contiene il codice della stanza: i tuoi amici atterrano direttamente al tuo tavolo.',
            'Puoi impostare la partita mentre la gente arriva: il modo, il numero di round, il tempo di disegno, la dimensione della casella e persino le categorie di immagini. Niente è fissato finché non lanci.',
          ],
        },
        {
          heading: 'Uno paga, giocano tutti',
          blocks: [
            'Due modi sono gratis per sempre: la caccia comune, che è quello predefinito, e il modo stream.',
            'Gli altri diventeranno a pagamento, ma solo alla CREAZIONE di una partita. Unirsi resterà gratis, senza condizioni. In pratica basta che una sola persona del gruppo abbia sbloccato il gioco perché tutto il tavolo goda di tutti i modi, senza account e senza pagare nulla.',
            'Lo sblocco è un acquisto unico, senza abbonamento.',
          ],
        },
        {
          heading: 'Quale modo per quale gruppo',
          blocks: [
            'In tre o quattro, la caccia comune basta e si capisce in un round. Da sei in su diventa davvero buona: le scoperte si susseguono e lo schermo non smette di muoversi.',
            'Per chiudere una serata, provate il battle royale: il tavolo si svuota round dopo round e l’ultimo si gioca in due. Per un gruppo misto, in cui qualcuno preferisce disegnare con calma, il concorso toglie tutta la pressione del cronometro della caccia.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-en-stream',
      title: 'Un gioco di disegno da fare in diretta',
      date: '2026-08-21',
      summary:
        'Retusch ha un modo pensato per la diretta, in cui lo streamer cerca da solo mentre il suo pubblico si nasconde. Come usarlo, e quali altri modi funzionano davanti a un pubblico.',
      sections: [
        {
          heading: 'Un modo fatto apposta',
          blocks: [
            'Il modo stream inverte i ruoli. Tutti i giocatori disegnano sullo stesso quadro tranne chi ospita, che non disegna affatto. Poi chi ospita è l’unico a cercare, mentre il suo pubblico guarda.',
            'È esattamente la situazione che una diretta richiede, e per un motivo preciso: siccome chi ospita non disegna, il suo nascondiglio non esiste, e la chat non ha visto nulla che non dovesse vedere. Gli spettatori conoscono invece i nascondigli degli altri giocatori, e possono guardarlo passare accanto tre volte di fila.',
            'Per questo caso sono state scritte due regole. Lo streamer non fa punti e non compare in classifica: la partita si gioca fra chi si nasconde, lui conduce lo spettacolo. E può sbagliare quante volte vuole, mentre tutti gli altri modi si fermano al primo errore. Una diretta non può finire su un clic sfortunato.',
            'Il modo stream è gratis, e resterà così.',
          ],
        },
        {
          heading: 'Il concorso, la seconda buona idea',
          blocks: [
            'Nel modo concorso, tutti ridipingono la stessa casella dello stesso quadro, poi ciascuno vota il ritocco che preferisce. Non c’è più niente da trovare, solo da giudicare.',
            'Davanti a un pubblico funziona benissimo, e per lo stesso motivo del modo stream: non c’è niente da trovare, quindi niente che la chat possa rovinare. Le proposte si confrontano affiancate, il voto dà un momento di suspense, e la rivelazione dei nomi offre una chiusura netta. È anche il modo più corto.',
          ],
        },
        {
          heading: 'E gli altri modi',
          blocks: [
            'Niente obbliga a un modo dedicato, ma allora conviene giocare con degli amici e non con la chat. In tutti gli altri modi lo streamer dipinge la propria casella sullo schermo: gli spettatori la vedono costruirsi e conoscono la risposta prima ancora che la caccia cominci.',
            'Una partita fra amici, invece, si segue benissimo da fuori: la fase di disegno mostra ciascuno costruire il proprio nascondiglio, e la rivelazione scopre tutte le caselle in un colpo, con il nome dell’autore.',
          ],
        },
        {
          heading: 'Far entrare la chat nella partita',
          blocks: [
            'Il link d’invito è l’unica cosa da condividere. Incollalo in chat e i primi arrivati riempiono il tavolo: nessun account da creare, nessun download, nessuna coda.',
            'Il limite di giocatori si imposta nella stanza, fra 2 e 10. Con un pubblico numeroso, apri più partite brevi invece di una lunga: un round dura meno di tre minuti.',
          ],
        },
      ],
    },
    {
      slug: 'nouveautes',
      title: 'Le novità di Retusch',
      date: '2026-08-21',
      summary:
        'Le modifiche di gioco introdotte in Retusch, dalla più recente alla più vecchia: nuovi modi, nuove regole, miglioramenti al disegno e alla caccia.',
      sections: [
        {
          heading: 'Agosto 2026',
          blocks: [
            {
              list: [
                'La casella da ridipingere non parte più da un quadrato bianco, ma dal colore dominante di ciò che copre. Non si perde più metà del tempo a ridipingere un fondo che nel quadro non esiste.',
                'Il secchiello non lascia più un bordo chiaro lungo i tratti: ora scivola sotto il loro bordo sfumato.',
                'Il battle royale non conta più punti. Il primo giocatore scoperto lascia la partita, e ciascuno riceve una posizione di round mostrata sulla sua casella, dal rosso al verde.',
                'Il modo stream dà allo streamer tutti i tentativi che vuole: il primo errore non chiude più il round.',
                'In modo spettatore si può ora spostare e ingrandire il quadro che si guarda costruire.',
                'Le reazioni emoji partono dal nome del loro autore, nella lista dei giocatori, invece di salire dal basso dello schermo.',
                'Fuori dalla casella da ridipingere, pennello e secchiello spostano la vista: non serve più prendere lo strumento mano per muoversi.',
                'Il nome del gioco, in alto a sinistra durante una partita, riporta alla pagina iniziale dopo conferma.',
                'Giapponese e coreano si aggiungono alle quindici lingue già disponibili.',
              ],
            },
          ],
        },
        {
          heading: 'Luglio 2026',
          blocks: [
            {
              list: [
                'Nuovo modo concorso: tutti ridipingono la stessa casella, poi ciascuno vota il ritocco che preferisce.',
                'Nuovo modo stream, pensato per la diretta: chi ospita cerca da solo mentre il suo pubblico si nasconde.',
                'Le categorie di immagini sostituiscono i livelli di difficoltà. Si spuntano quelle che si vogliono vedere, e ogni categoria mostra un quadro di esempio.',
                'Due round di fila non cadono più sullo stesso quadro, e le categorie spuntate si alternano da un round all’altro.',
                'La galleria di fine partita permette di rivedere un ritocco tratto per tratto e di esportarlo come immagine o come GIF animata.',
              ],
            },
          ],
        },
      ],
    },
  ],

  ui: {
    atAGlance: 'In sintesi',
    allArticles: 'Tutti gli articoli',
    theRules: 'Le regole del gioco',
    theModes: 'I modi di gioco',
  },
};
